import { useRouteError } from "react-router-dom";
import { captureException, showReportDialog } from "@sentry/react";
import { memo, useEffect, useState } from "react";
import {
    getDynamicImportFailure,
    isChunkLoadError,
    isModuleParseFailure,
    reloadOnceForChunkError,
} from "utilities-for/chunk-load-error/chunk-load-error";
import ErrorFallback from "../error-fallback";

/**
 * React Router error boundary fallback that reports errors to Sentry and
 * recovers from transient chunk loading failures.
 *
 * - For chunk load errors (typically a stale deployment), reloads the page once
 *   to fetch the current chunks, without flashing the generic crash UI or
 *   reporting to Sentry.
 * - If the reload does not resolve the failure, reports it to Sentry tagged as an
 *   asset-load failure and shows retry guidance. Deliberately *not* the crash UI
 *   and crash-report dialog: the app did not crash, a file failed to arrive
 *   intact, and asking the user to file a crash report for that is noise (see
 *   GAS-1043, where users were shown a crash report dialog for a chunk that would
 *   not parse).
 * - For any other error — including a module that imported fine and then threw on
 *   initialisation, which is a bug rather than a bad download — captures the
 *   exception, opens the report dialog, and renders the crash UI.
 *
 * @returns {JSX.Element | null} `null` while reloading after a chunk error, or the `ErrorFallback` UI otherwise.
 */
export function SentryErrorFallback() {
    const error = useRouteError();
    // An asset problem however the browser phrased it: either a recognised chunk
    // message, or a tagged import that failed to *parse* (a chunk that fetched fine
    // but whose bytes were not JavaScript names no chunk and has no stack).
    //
    // Deliberately not "any tagged import failure": a module that ran and threw
    // rejects the same import, but that is an application bug and must keep the
    // crash UI and its report dialog.
    const isAssetLoadFailure = isChunkLoadError(error) || isModuleParseFailure(error);
    // Optimistically render nothing for asset failures so we don't flash the crash
    // UI while attempting a reload; other errors show the fallback immediately.
    const [showFallback, setShowFallback] = useState(() => !isAssetLoadFailure);

    useEffect(() => {
        if (!error) {
            return;
        }

        if (isAssetLoadFailure) {
            if (reloadOnceForChunkError()) {
                // Page is reloading — don't report or render the crash UI.
                return;
            }

            // A reload did not recover, so the same bytes are being re-served.
            // Report it for visibility, with whatever the import boundary recorded,
            // but don't ask the user for a crash report.
            setShowFallback(true);
            captureException(error, {
                tags: { errorKind: "asset-load" },
                extra: { ...getDynamicImportFailure(error) },
            });
            return;
        }

        // A genuine application error.
        setShowFallback(true);
        const eventId = captureException(error);
        showReportDialog({ eventId: eventId ?? undefined });
    }, [error, isAssetLoadFailure]);

    if (!showFallback) {
        return null;
    }

    return <ErrorFallback variant={isAssetLoadFailure ? "assetLoad" : "crash"} />;
}

export default memo(SentryErrorFallback);
